import { Filter, RelatedTag } from '@empathy/search-types';
import { GETTERS_KEYS, URLDefaultMappings, URLStoreMode } from '@empathybroker/ebx-gluon';
import { URLAction, URLContext } from '@empathybroker/ebx-gluon/dist/store/url/url.interfaces';

export class StoreURLAction implements URLAction {

  actions = {
    ['store']: (context: URLContext, mode: URLStoreMode = URLStoreMode.NEW): any => {
      this.clearSearchQueryParameters(context);
      this.addSearchParameters(context);
      this.storeURLInHistory(context, mode);
    }
  };

  private clearSearchQueryParameters(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    Object.values(queryDefaultMappings).forEach(queryParam => searchParams.delete(queryParam));
    return searchParams;
  }

  private addSearchParameters(context: URLContext): URLSearchParams {
    const { searchParams } = this.getURLParamsAndMappings(context);
    if (context.rootState.search.query) {
      this.addQueryParameter(context);
      this.addFilterParameter(context);
      this.addPageParameter(context);
      this.addScrollParameter(context);
      this.addSortParameter(context);
      this.addRelatedTagsParameter(context);
    }
    return searchParams;
  }

  private storeURLInHistory({ commit, getters }: URLContext, mode: URLStoreMode) {
    const url_2: URL = getters['url'];
    const url = url_2.href;
    if (!history.state || url !== history.state.href) {
      if (mode === URLStoreMode.REPLACE) {
        history.replaceState({ url }, 'EmpathyX', url);
      } else {
        history.pushState({ url }, 'EmpathyX', url);
      }
      commit('setURL', url);
    }
  }

  private getURLParamsAndMappings(context: URLContext): { searchParams: URLSearchParams, queryDefaultMappings: URLDefaultMappings } {
    return {
      searchParams: context.getters['url'].searchParams,
      queryDefaultMappings: context.rootGetters[GETTERS_KEYS.config.url].queryDefaultMappings
    };
  }

  private addQueryParameter(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    searchParams.set(queryDefaultMappings.query, context.rootState.search.query);
    return searchParams;
  }

  private addFilterParameter(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const selectedFilters: Filter[] = context.rootGetters[GETTERS_KEYS.search.selectedFilters];
    selectedFilters.forEach((selectedFilter: Filter) => {
        searchParams.append(queryDefaultMappings.filters, selectedFilter.id);
    });
    return searchParams;
  }

  private addPageParameter(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const page = context.rootState.search.page;
    if (page > 1) {
      searchParams.set(queryDefaultMappings.page, page.toString());
    }
    return searchParams;
  }

  private addScrollParameter(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const scrollTop = context.rootState.core.scrollTop;
    if (scrollTop > 0) {
      searchParams.set(queryDefaultMappings.scroll, scrollTop.toString());
    }
    return searchParams;
  }

  private getFilterWithAncestors(filter: Filter): Filter[] {
    if (filter.parent) {
      return this.getFilterWithAncestors(filter.parent).concat([filter]);
    } else {
      return [filter];
    }
  }

  private addSortParameter(context: URLContext) {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const sortValue = context.rootState.search.sort.value;
    if (sortValue) {
      searchParams.set(queryDefaultMappings.sort, sortValue);
    }
  }

  private addRelatedTagsParameter(context: URLContext) {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const selectedRelatedTags: RelatedTag[] = context.rootGetters[GETTERS_KEYS.querySignals.selectedRelatedTags];
    selectedRelatedTags.forEach(relatedTag => {
      searchParams.append(queryDefaultMappings.relatedTags, relatedTag.tag);
    });
  }
}
